import {
  EventType,
  RegistrationAccessType,
  ScheduleItemV2,
  SegmentRegistrationStatus,
} from '@hopin-team/api-hopin';
import { Badge } from '@hopin-team/ui-badge';
import { Button, ButtonProps } from '@hopin-team/ui-button';
import { Flex } from '@hopin-team/ui-flex';
import { Icon } from '@hopin-team/ui-icon';
import { Modal, ModalProps } from '@hopin-team/ui-modal';
import { Text } from '@hopin-team/ui-text';
import { UseMutationResult } from '@tanstack/react-query';
import { AnalyticsInstance } from 'analytics';
import React, { useEffect, useMemo, useState } from 'react';

import { useLocales } from '../../hooks';
import { FullFlex } from '../Item';
import {
  BookedSeatContainer,
  CancelSeatButton,
  FullWidthBadge,
  PressedButton,
} from './BookASeatButton.styles';
import { OverlappingSegments } from './OverlappingSegments';
import { segmentStatusSelector } from './segment-status-selector';

export type BookASeatParams =
  | {
      cancelOverlappingSegments: true;
      cancelledSegmentIds: string[];
    }
  | undefined;

export type BookASeatHook = (hookProps: {
  client: string;
  eventSlug: string;
  segmentExternalId: string;
  scheduleItems?: ScheduleItemV2[];
  onCapacityReached: () => void;
  onUnknownError: () => void;
  onScheduleOverlap: (overlappingSegmentIds: string[]) => void;
  track?: AnalyticsInstance['track'];
}) => Pick<
  UseMutationResult<void, any, BookASeatParams, unknown>,
  'mutate' | 'mutateAsync' | 'isSuccess'
>;

export type CancelASeatHook = ({
  eventSlug,
  segmentExternalId,
  client,
  onUnknownError,
  track,
}: {
  client: string;
  eventSlug: string;
  segmentExternalId: string;
  onUnknownError: () => void;
  track?: AnalyticsInstance['track'];
}) => Pick<
  UseMutationResult<void, any, void, unknown>,
  'mutate' | 'mutateAsync' | 'isSuccess'
>;

export type BookASeatProps = {
  client: string;
  eventSlug: string;
  eventType: EventType;
  registrationAccessType: RegistrationAccessType | null;
  segmentExternalId: string;
  segmentsStatus?: SegmentRegistrationStatus[];
  track?: AnalyticsInstance['track'];
  onModalStateChange?: (isOpen: boolean) => void;
  hasEnded: boolean;
  isRegistration?: boolean;
  buttonText?: string;
  isSegmentSwitchingEnabled?: boolean;
  useBookASeat: BookASeatHook;
  useCancelASeat: CancelASeatHook;
  scheduleItems: ScheduleItemV2[];
  isOrganiserPass?: boolean;
} & ButtonProps;

enum SeatState {
  BOOKED = 'seat-booked',
  NOT_BOOKED = 'book-a-seat',
  FULLY_BOOKED = 'fully-booked',
}

const getBookASeatState = (
  isAlreadyBooked: boolean,
  remaining: number | null,
): SeatState => {
  if (isAlreadyBooked) {
    return SeatState.BOOKED;
  }
  if (remaining !== null && remaining < 1) {
    return SeatState.FULLY_BOOKED;
  }

  return SeatState.NOT_BOOKED;
};

const modalDialogProps: Partial<ModalProps> = {
  type: 'dialog',
  size: 'small',
  withCloseButton: true,
  isDismissible: true,
  withSeparator: 'footer',
};

enum MODAL {
  CAPACITY_REACHED = 'capacity-reached',
  CANCEL_SEAT = 'cancel-seat',
  SOMETHING_WENT_WRONG = 'something-went-wrong',
  OVERLAPPING_SCHEDULE = 'overlapping-schedule',
}

export const BookASeatButton = ({
  client,
  eventType,
  eventSlug,
  segmentsStatus,
  registrationAccessType,
  segmentExternalId = '',
  onModalStateChange,
  useBookASeat,
  useCancelASeat,
  track,
  hasEnded,
  isRegistration,
  isSegmentSwitchingEnabled,
  scheduleItems,
  buttonText,
  isOrganiserPass = false,
  ...buttonProps
}: BookASeatProps) => {
  const { t } = useLocales('reception');
  const [openedModal, setModal] = useState<MODAL | undefined>();
  const [overlappingSegmentIds, setOverlappingSegments] = useState<
    string[] | undefined
  >();

  const closeModal = (
    e: React.MouseEvent<HTMLButtonElement & HTMLAnchorElement>,
  ) => {
    e.stopPropagation();
    setModal(undefined);
  };

  const { isAlreadyBooked, remainingCapacity } = useMemo(
    () => segmentStatusSelector({ segmentsStatus, segmentExternalId }),
    [segmentsStatus, segmentExternalId],
  );

  const seatState = useMemo(
    () => getBookASeatState(isAlreadyBooked, remainingCapacity),
    [isAlreadyBooked, remainingCapacity],
  );

  const { mutate: bookASeat } = useBookASeat({
    client,
    eventSlug,
    segmentExternalId,
    scheduleItems,
    onCapacityReached: () => {
      setModal(MODAL.CAPACITY_REACHED);
    },
    track,
    onUnknownError: () => {
      setModal(MODAL.SOMETHING_WENT_WRONG);
    },
    onScheduleOverlap: overlappingSegmentIds => {
      if (!isSegmentSwitchingEnabled) {
        setModal(MODAL.SOMETHING_WENT_WRONG);
        return;
      }
      setModal(MODAL.OVERLAPPING_SCHEDULE);
      setOverlappingSegments(overlappingSegmentIds);
    },
  });
  const { mutate: cancelASeat } = useCancelASeat({
    client,
    eventSlug,
    segmentExternalId,
    track,
    onUnknownError: () => {
      setModal(MODAL.SOMETHING_WENT_WRONG);
    },
  });

  useEffect(() => {
    onModalStateChange?.(openedModal !== undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openedModal]);

  if (
    hasEnded ||
    eventType === 'virtual' ||
    (registrationAccessType === 'virtual' && !isOrganiserPass)
  ) {
    return null;
  }

  const handleBookASeat = (
    e: React.MouseEvent<HTMLButtonElement & HTMLAnchorElement>,
  ) => {
    e.stopPropagation();
    bookASeat(undefined);
  };

  const handleCancelSeatClick = (
    e: React.MouseEvent<HTMLButtonElement & HTMLAnchorElement>,
  ) => {
    e.stopPropagation();
    setModal(MODAL.CANCEL_SEAT);
  };

  const handleSegmentSwitching = (
    e: React.MouseEvent<HTMLButtonElement & HTMLAnchorElement>,
  ) => {
    e.stopPropagation();
    bookASeat({
      cancelOverlappingSegments: true,
      cancelledSegmentIds: overlappingSegmentIds as string[],
    });
    closeModal(e);
    setOverlappingSegments(undefined);
  };

  const handleCancelSeatBooking = (
    e: React.MouseEvent<HTMLButtonElement & HTMLAnchorElement>,
  ) => {
    e.stopPropagation();
    cancelASeat();
    closeModal(e);
  };

  const somethingWentWrongDialog = (
    <Modal
      {...modalDialogProps}
      isShowing={openedModal === MODAL.SOMETHING_WENT_WRONG}
      title={t('something-went-wrong')}
      subtext={t('request-failed')}
      withSeparator="footer"
      footer={
        <Button pattern="primary" onClick={closeModal}>
          {t('go-back')}
        </Button>
      }
      onClose={closeModal}
    />
  );

  const capacityReachedDialog = (
    <Modal
      {...modalDialogProps}
      isShowing={openedModal === MODAL.CAPACITY_REACHED}
      title={t('session-fully-booked')}
      subtext={eventType === 'hybrid' ? t('seat-not-available') : undefined}
      withSeparator="footer"
      footer={
        <Button pattern="primary" onClick={closeModal}>
          {t('go-back')}
        </Button>
      }
      onClose={closeModal}
    />
  );

  const overlappingScheduleDialog = (
    <Modal
      {...modalDialogProps}
      size="medium"
      isShowing={openedModal === MODAL.OVERLAPPING_SCHEDULE}
      title={t('segment-overlaps')}
      subtext={t('this-segment-overlaps', {
        count: overlappingSegmentIds?.length,
      })}
      isDismissible
      onClose={closeModal}
      footer={[
        <Button key={0} pattern="primary" isOutlined onClick={closeModal}>
          {t('keep-previous-seat', {
            count: overlappingSegmentIds?.length,
          })}
        </Button>,
        <Button
          ml={2}
          key={1}
          pattern="primary"
          onClick={handleSegmentSwitching}
        >
          {t('switch-seat')}
        </Button>,
      ]}
    >
      {overlappingSegmentIds && (
        <OverlappingSegments
          segmentIds={overlappingSegmentIds}
          scheduleItems={scheduleItems}
        />
      )}
    </Modal>
  );

  const cancelConfirmDialog = (
    <Modal
      {...modalDialogProps}
      isShowing={openedModal === MODAL.CANCEL_SEAT}
      title={t('unbook-your-seat')}
      subtext={
        eventType === 'onsite'
          ? t('not-able-to-access-onsite')
          : t('not-able-to-access-hybrid')
      }
      withSeparator="footer"
      size="medium"
      footer={[
        <Button key={0} pattern="primary" isOutlined onClick={closeModal}>
          {t('keep-seat')}
        </Button>,
        <Button key={1} pattern="danger" onClick={handleCancelSeatBooking}>
          {t('cancel-booking')}
        </Button>,
      ]}
      withCloseButton={false}
      onClose={closeModal}
    />
  );

  let bookASeatComponent;
  if (seatState === SeatState.BOOKED) {
    bookASeatComponent = isRegistration ? (
      <PressedButton
        mt={1}
        px={4}
        size={'small'}
        {...buttonProps}
        onClick={handleCancelSeatBooking}
        isGhost
        leadingIcon="success"
      >
        {t('selected')}
      </PressedButton>
    ) : (
      <BookedSeatContainer>
        <FullWidthBadge
          label={t(seatState)}
          colorPattern="success"
          priority="low"
          leadingIcon="checkmark"
        />
        <CancelSeatButton
          data-testid="cancel-a-seat"
          ml={1}
          p={1}
          isOutlined
          title={t('cancel-booking')}
          icon="close-circle"
          pattern="secondary"
          onClick={handleCancelSeatClick}
          size="tiny"
        />
        {cancelConfirmDialog}
      </BookedSeatContainer>
    );
  } else if (seatState === SeatState.FULLY_BOOKED) {
    bookASeatComponent = (
      <>
        <Badge
          label={t(seatState)}
          colorPattern="inactive"
          priority="high"
          leadingIcon="disable"
          px={2}
        />
        {capacityReachedDialog}
      </>
    );
  } else {
    bookASeatComponent = (
      <FullFlex flexDirection="column">
        {remainingCapacity !== null && (
          <Flex alignItems="center">
            <Icon name="ticket" color="grey-500" mr={1} />
            <Text pattern="captionMedium" color="grey-600">
              {t('remaining-capacity', { count: remainingCapacity })}
            </Text>
          </Flex>
        )}

        <Button
          mt={1}
          px={4}
          size={'small'}
          {...buttonProps}
          onClick={handleBookASeat}
          disabled={seatState !== SeatState.NOT_BOOKED}
          isOutlined
        >
          {buttonText || t(seatState)}
        </Button>
      </FullFlex>
    );
  }

  return (
    <Flex flexDirection="row" alignItems="center">
      {bookASeatComponent}
      {overlappingScheduleDialog}
      {somethingWentWrongDialog}
    </Flex>
  );
};
